import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Calendar as CalIcon, Clock, Users, MapPin } from "lucide-react";

const STATUS_LABELS = {
  da_confermare: "Da confermare",
  seduto: "Seduto", // deprecated, backend coerces to arrivato — kept only for stale caches
  arrivato: "Arrivato", completata: "Completata",
  annullata: "Annullata", no_show: "No-show",
};

export default function ReservationConfirm() {
  const { token } = useParams();
  const [resv, setResv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({
    customer_email: "", allergies: "", notes: "",
    privacy_consent: true, marketing_consent: false,
  });

  useEffect(() => {
    let mounted = true;
    api.get(`/public/reservations/${token}`)
      .then((r) => {
        if (!mounted) return;
        setResv(r.data);
        setForm((f) => ({
          ...f,
          customer_email: r.data.customer_email || "",
          allergies: (r.data.allergies || []).join(", "),
          privacy_consent: !!r.data.privacy_consent || true,
          marketing_consent: !!r.data.marketing_consent,
        }));
      })
      .catch(() => { if (mounted) setNotFound(true); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [token]);

  const confirmed = resv?.status && resv.status !== "da_confermare"
                    && resv.status !== "annullata";

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_email: form.customer_email || null,
        allergies: (form.allergies || "").split(",").map((x) => x.trim()).filter(Boolean),
        notes: form.notes || null,
        privacy_consent: !!form.privacy_consent,
        marketing_consent: !!form.marketing_consent,
      };
      const r = await api.post(`/public/reservations/${token}/confirm`, payload);
      setResv(r.data);
      toast.success("Prenotazione confermata. Grazie!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <Layout><div className="text-center py-16 text-ink-500">Caricamento…</div></Layout>;
  }
  if (notFound || !resv) {
    return (
      <Layout>
        <div className="text-center py-16" data-testid="resv-confirm-notfound">
          <div className="text-5xl mb-3">🔗</div>
          <div className="font-serif text-2xl">Link non valido</div>
          <div className="text-ink-500 mt-1 text-sm">
            La prenotazione non esiste o il link è scaduto. Chiama lo stabilimento per assistenza.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-md mx-auto" data-testid="resv-confirm-page">
        <div className="text-center">
          <div className="eyebrow">Sarabi Beach</div>
          <h1 className="font-serif text-3xl mt-1">La tua prenotazione</h1>
          <p className="text-sm text-ink-500 mt-1">Codice <b className="font-mono">{resv.code}</b></p>
        </div>

        <div className="mt-6 rounded-2xl border border-sand-300 bg-white p-5 space-y-2 text-sm">
          <Row Icon={CalIcon} label="Data" value={new Date(resv.date + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })} />
          <Row Icon={Clock}   label="Ora"  value={resv.time} />
          <Row Icon={Users}   label="Coperti" value={`${resv.pax} persone`} />
          {resv.zone && <Row Icon={MapPin} label="Zona" value={resv.zone} />}
          {resv.occasion && <Row Icon={CheckCircle2} label="Occasione" value={resv.occasion} />}
        </div>

        {confirmed && (
          <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-emerald-900 text-sm flex items-center gap-2"
               data-testid="resv-confirmed-badge">
            <CheckCircle2 className="w-5 h-5" />
            Prenotazione {STATUS_LABELS[resv.status]?.toLowerCase()}. Puoi comunque aggiornare i dati qui sotto.
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-sand-300 bg-white p-5 space-y-4">
          <div>
            <Label className="text-xs text-ink-500">Email (opzionale)</Label>
            <Input type="email" value={form.customer_email}
                   onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                   className="mt-1" data-testid="confirm-email" />
          </div>
          <div>
            <Label className="text-xs text-ink-500">Allergie / intolleranze (separate da virgola)</Label>
            <Input value={form.allergies}
                   onChange={(e) => setForm({ ...form, allergies: e.target.value })}
                   className="mt-1" placeholder="glutine, arachidi…" data-testid="confirm-allergies" />
          </div>
          <div>
            <Label className="text-xs text-ink-500">Note per la sala</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={2} className="mt-1" data-testid="confirm-notes" />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={form.privacy_consent} className="mt-1"
                   onChange={(e) => setForm({ ...form, privacy_consent: e.target.checked })}
                   data-testid="confirm-privacy" />
            <span>Ho letto e accetto l'<a href="#" className="underline">informativa privacy</a>. *</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={form.marketing_consent} className="mt-1"
                   onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
                   data-testid="confirm-marketing" />
            <span>Accetto di ricevere offerte e novità di Sarabi Beach.</span>
          </label>
          <Button className="w-full" onClick={submit} disabled={saving}
                  data-testid="confirm-submit">
            {saving ? "Salvo…" : (confirmed ? "Aggiorna dettagli" : "Conferma prenotazione")}
          </Button>
        </div>

        <div className="text-center text-xs text-ink-400 mt-6">Sarabi Beach · Grazie per aver scelto di essere con noi.</div>
      </div>
    </Layout>
  );
}

function Row({ Icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4 text-bronze-600" strokeWidth={1.5} />
      <div className="text-ink-500 w-24">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

function Layout({ children }) {
  return (
    <div className="min-h-screen bg-sand-50 py-10 px-4">
      {children}
    </div>
  );
}
